/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V.
 * Gegenereerd met Junie (AI), 24-06-2026
 *
 * Generieke attribute-directive die het host-element laat meebewegen met een
 * Cytoscape-node. Zet op het host-element de CSS custom properties
 * `--dl-volger-top`, `--dl-volger-left`, `--dl-volger-width`, `--dl-volger-height`
 * en `--dl-volger-zoom`, en positioneert het host-element absoluut via
 * `transform: translate(...)`. Volgers van onzichtbare (of net verwijderde)
 * nodes worden volledig verborgen via een `display: none`-hostbinding.
 *
 * Inputs:
 * - `dlNodeVolger` (verplicht): de Cytoscape-node die gevolgd moet worden.
 * - `cy` (verplicht): de Cytoscape-`Core` waarin de node leeft.
 * - `ankertype` (optioneel, default `'bbox'`): bepaalt op welk punt van de
 *   rendered bounding box het host-element wordt verankerd.
 * - `volgFormaat` (optioneel, default `true`): als `true` krijgt het host-element
 *   width/height van de bbox; als `false` wordt het een 0×0 puntanker.
 * - `schaalMetZoom` (optioneel, default `false`): als `true` past de directive
 *   een `transform: scale(zoom)` toe op het host-element zodat de inhoud mee
 *   schaalt met de cytoscape-zoom; default niet (vaste pixelgrootte).
 * - `offset` (optioneel): fijnafstemming in pixels (na ankerberekening en
 *   eventuele schaal toegepast).
 * - `zichtbaarAls` (optioneel): extra zichtbaarheidscheck; geeft deze false
 *   voor de gevolgde node, dan wordt de volger verborgen.
 */
import { Directive, ElementRef, Input, NgZone, OnChanges, OnDestroy, inject, signal } from '@angular/core';
import type { Core, NodeSingular } from 'cytoscape';

/**
 * Mogelijke ankertypes voor het host-element t.o.v. de rendered bounding box.
 * - `bbox`: host krijgt positie + (afhankelijk van `volgFormaat`) afmetingen van de bbox.
 * - hoeken/zijden: host wordt verankerd op het bijbehorende punt van de bbox.
 * - `midden`: anker op het midden van de bbox.
 */
export type DlNodeVolgerAnker =
    | 'bbox'
    | 'linksboven' | 'boven' | 'rechtsboven'
    | 'links' | 'midden' | 'rechts'
    | 'linksonder' | 'onder' | 'rechtsonder';

/**
 * Pixel-offset die ná de ankerberekening (en schaaltoepassing) op de positie
 * van het host-element wordt opgeteld.
 */
export interface DlNodeVolgerOffset {
    x?: number;
    y?: number;
}

@Directive({
    selector: '[dlNodeVolger]',
    standalone: true,
    host: {
        'class': 'dl-node-volger',
        'style': 'position:absolute;top:0;left:0;pointer-events:none;',
        '[style.display]': "zichtbaar() ? null : 'none'"
    }
})
export class DlNodeVolgerDirective implements OnChanges, OnDestroy {

    /**
     * De Cytoscape-node die gevolgd moet worden.
     */
    @Input({ required: true }) dlNodeVolger!: NodeSingular;

    /**
     * De Cytoscape-`Core` waarin de node leeft. Nodig om viewport-events op te vangen.
     */
    @Input({ required: true }) cy!: Core;

    /**
     * Bepaalt op welk punt van de bbox het host-element wordt verankerd.
     * Default `'bbox'` (het host-element bedekt de hele bbox, gedrag identiek aan v1 van de directive).
     */
    @Input() ankertype: DlNodeVolgerAnker = 'bbox';

    /**
     * Als `true` krijgt het host-element width/height van de bbox; als `false`
     * wordt het een 0×0 puntanker waaromheen de inhoud zich vrij opbouwt.
     * Default `true` voor backward compatibility.
     */
    @Input() volgFormaat: boolean = true;

    /**
     * Als `true` past de directive `transform: scale(zoom)` toe zodat de inhoud
     * mee schaalt met de cytoscape-zoom. Default `false` — menu's en knoppen
     * willen meestal een vaste pixelgrootte.
     */
    @Input() schaalMetZoom: boolean = false;

    /**
     * Pixel-offset op de uiteindelijke positie van het host-element.
     */
    @Input() offset: DlNodeVolgerOffset | null = null;

    /**
     * Optionele extra zichtbaarheidscheck; bij elke update aangeroepen met de
     * gevolgde node. Geeft de check false, dan wordt de volger verborgen.
     */
    @Input() zichtbaarAls: ((node: NodeSingular) => boolean) | null = null;

    private readonly host: HTMLElement = inject(ElementRef).nativeElement;
    private readonly zone = inject(NgZone);

    /**
     * Of de gevolgde node zichtbaar is; onzichtbare nodes krijgen geen volger-weergave.
     */
    protected readonly zichtbaar = signal(true);

    private gekoppeldeNode: NodeSingular | null = null;
    private gekoppeldeCy: Core | null = null;
    private readonly updateHandler = (): void => this.update();

    /**
     * Re-koppelt event-listeners wanneer de node of cy verandert, en doet
     * altijd een nieuwe positie-update zodat ook ankertype-/offset-wijzigingen
     * direct effect hebben.
     */
    ngOnChanges(): void {
        if (this.gekoppeldeNode !== this.dlNodeVolger || this.gekoppeldeCy !== this.cy) {
            this.ontkoppel();
            this.koppel();
        }
        this.update();
    }

    /**
     * Ruimt event-listeners op bij verwijderen van het host-element.
     */
    ngOnDestroy(): void {
        this.ontkoppel();
    }

    /**
     * Koppelt position- en viewport-events buiten Angular's zone, om change
     * detection niet onnodig te triggeren tijdens pannen/zoomen.
     */
    private koppel(): void {
        if (!this.dlNodeVolger || !this.cy) return;
        this.gekoppeldeNode = this.dlNodeVolger;
        this.gekoppeldeCy = this.cy;
        this.zone.runOutsideAngular(() => {
            this.gekoppeldeNode!.on('position', this.updateHandler);
            this.gekoppeldeCy!.on('viewport render', this.updateHandler);
        });
    }

    /**
     * Verwijdert eerder gekoppelde event-listeners.
     */
    private ontkoppel(): void {
        if (this.gekoppeldeNode) {
            this.gekoppeldeNode.off('position', undefined as any, this.updateHandler);
            this.gekoppeldeNode = null;
        }
        if (this.gekoppeldeCy) {
            this.gekoppeldeCy.off('viewport render', this.updateHandler);
            this.gekoppeldeCy = null;
        }
    }

    /**
     * Berekent de rendered bounding box van de node, leidt daar het ankerpunt
     * en de afmetingen uit af, en zet positie/afmetingen en CSS custom
     * properties op het host-element.
     */
    private update(): void {
        if (!this.gekoppeldeNode || !this.gekoppeldeCy) return;
        //volgers van onzichtbare (of net verwijderde) nodes volledig verbergen;
        //visible() is op een verwijderd element niet betrouwbaar, vandaar inside()
        this.zichtbaar.set(
            this.gekoppeldeNode.inside()
            && this.gekoppeldeNode.visible()
            && (this.zichtbaarAls?.(this.gekoppeldeNode) ?? true)
        );
        if (!this.zichtbaar()) return;
        const bb = this.gekoppeldeNode.renderedBoundingBox({ includeLabels: false });
        const zoom = this.gekoppeldeCy.zoom();

        const ankerX = this.ankerXVoor(bb);
        const ankerY = this.ankerYVoor(bb);
        const offsetX = this.offset?.x ?? 0;
        const offsetY = this.offset?.y ?? 0;
        const x = ankerX + offsetX;
        const y = ankerY + offsetY;

        const breedte = this.volgFormaat ? bb.w : 0;
        const hoogte = this.volgFormaat ? bb.h : 0;

        const style = this.host.style;
        style.setProperty('--dl-volger-top', `${y}px`);
        style.setProperty('--dl-volger-left', `${x}px`);
        style.setProperty('--dl-volger-width', `${breedte}px`);
        style.setProperty('--dl-volger-height', `${hoogte}px`);
        style.setProperty('--dl-volger-zoom', `${zoom}`);
        style.width = `${breedte}px`;
        style.height = `${hoogte}px`;
        style.transform = this.schaalMetZoom
            ? `translate(${x}px, ${y}px) scale(${zoom})`
            : `translate(${x}px, ${y}px)`;
    }

    /**
     * Berekent de x-coördinaat van het ankerpunt op basis van `ankertype` en de bbox.
     */
    private ankerXVoor(bb: { x1: number; x2: number; w: number }): number {
        switch (this.ankertype) {
            case 'bbox':
            case 'linksboven':
            case 'links':
            case 'linksonder':
                return bb.x1;
            case 'boven':
            case 'midden':
            case 'onder':
                return bb.x1 + bb.w / 2;
            case 'rechtsboven':
            case 'rechts':
            case 'rechtsonder':
                return bb.x2;
        }
    }

    /**
     * Berekent de y-coördinaat van het ankerpunt op basis van `ankertype` en de bbox.
     */
    private ankerYVoor(bb: { y1: number; y2: number; h: number }): number {
        switch (this.ankertype) {
            case 'bbox':
            case 'linksboven':
            case 'boven':
            case 'rechtsboven':
                return bb.y1;
            case 'links':
            case 'midden':
            case 'rechts':
                return bb.y1 + bb.h / 2;
            case 'linksonder':
            case 'onder':
            case 'rechtsonder':
                return bb.y2;
        }
    }
}
